import { apiClient } from './client';

/** Откуда берётся Собрано: платежи из базы или ручные Начисления. */
export type GrossSource = 'payments' | 'manual';

export interface CashflowProvider {
  id: string;
  code: string;
  name: string;
  commission_percent: number;
  /** С этой даты удерживается комиссия; раньше — льготный период. */
  commission_from: string | null;
  source: GrossSource;
  /** Валюта расчётов: UZS у локальных, USD у Apple. */
  currency: string;
  is_active: boolean;
  sort_order: number;
  /** Счета отправителя для Импорта выписки; null — ещё не настраивали. */
  sender_accounts: string[] | null;
  created_at: string;
  updated_at: string;
}

export interface Settlement {
  id: string;
  date: string; // YYYY-MM-DD
  provider_id: string;
  provider: CashflowProvider;
  /** Сумма в валюте провайдера. */
  amount: number;
  currency: string;
  /** Сумов за единицу валюты на момент ввода. */
  rate: number;
  comment: string | null;
  /** Отпечаток Проводки: есть — Поступление подтверждено выпиской банка (ADR 0026). */
  bank_fingerprint: string | null;
  bank_document: string | null;
  bank_posted_at: string | null;
  bank_sender_account: string | null;
  created_at: string;
  updated_at: string;
}

// ─── Импорт выписки (ADR 0026) ────────────────────────────────────────────────

export type StatementEntryStatus = 'new' | 'matched_manual' | 'imported' | 'foreign' | 'debit';

export interface StatementEntry {
  account: string;
  date: string;
  time: string;
  document: string;
  senderAccount: string | null;
  senderName: string;
  purpose: string;
  credit: number;
  debit: number;
  fingerprint: string;
  status: StatementEntryStatus;
  providerCode: string | null;
  providerName: string | null;
}

export interface StatementPreview {
  summary: Record<StatementEntryStatus, { count: number; sum: number }>;
  warnings: string[];
  entries: StatementEntry[];
}

export interface StatementImportResult {
  created: number;
  linked: number;
  skipped: number;
  preview: StatementPreview;
}

/** Покупатель в строке Начисления — ровно столько, чтобы видеть, выдан ли премиум. */
export interface AccrualUser {
  id: string;
  username: string;
  email: string | null;
  isSubscribed: boolean;
  subscribedEnd: string | null;
}

/** Ручная запись о продаже у провайдера, чьих платежей нет в базе (Apple). */
export interface Accrual {
  id: string;
  date: string; // YYYY-MM-DD
  provider_id: string;
  provider: CashflowProvider;
  amount: number;
  currency: string;
  rate: number;
  user_id: string | null;
  user: AccrualUser | null;
  comment: string | null;
  source: 'manual' | 'revenuecat';
  external_id: string | null;
  created_at: string;
  updated_at: string;
}

/** Одна карточка сверки: собрано → чистое → поступило → осталось получить. */
export interface ProviderCashflow {
  providerId: string;
  code: string;
  name: string;
  commissionPercent: number;
  commissionFrom: string | null;
  isActive: boolean;
  source: GrossSource;
  currency: string;
  /** Курс переоценки долга: последний введённый по провайдеру. */
  rate: number;
  gross: number;
  net: number;
  settled: number;
  /** Долг провайдера — всегда за всё время, период на него не влияет. */
  outstanding: number;
  /** Те же величины в сумах — из них складывается общий итог. */
  grossUzs: number;
  netUzs: number;
  settledUzs: number;
  outstandingUzs: number;
}

export interface CashflowTotals {
  gross: number;
  net: number;
  settled: number;
  outstanding: number;
}

export interface CashflowOverview {
  period: { from?: string; to?: string };
  providers: ProviderCashflow[];
  total: CashflowTotals;
  daily: Array<{ date: string; code: string; gross: number; grossUzs: number }>;
  settlements: Settlement[];
  accruals: Accrual[];
}

export interface CashflowPeriodParams {
  from?: string;
  to?: string;
}

export interface CreateSettlementDto {
  date: string;
  providerId: string;
  amount: number;
  /** Курс к суму; для сумовых провайдеров не отправляется. */
  rate?: number;
  comment?: string | null;
}

export type UpdateSettlementDto = Partial<CreateSettlementDto>;

export interface CreateAccrualDto {
  date: string;
  providerId: string;
  amount: number;
  rate?: number;
  userId?: string | null;
  comment?: string | null;
}

export type UpdateAccrualDto = Partial<CreateAccrualDto>;

export interface UpdateProviderDto {
  name?: string;
  commissionPercent?: number;
  /** Пустая строка снимает дату: комиссия действует на всю историю. */
  commissionFrom?: string | null;
  isActive?: boolean;
  sortOrder?: number;
  /** Счета отправителя для Импорта выписки. */
  senderAccounts?: string[];
}

export interface CreateProviderDto {
  code: string;
  name: string;
  commissionPercent: number;
  commissionFrom?: string | null;
  sortOrder?: number;
}

// У apiClient по умолчанию JSON — axios превратил бы FormData в JSON, поэтому явно
const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

const statementForm = (file: File) => {
  const form = new FormData();
  form.append('file', file);
  return form;
};

export const cashflowApi = {
  getOverview: async (params: CashflowPeriodParams = {}): Promise<CashflowOverview> => {
    const response = await apiClient.get<CashflowOverview>('/cashflow/overview', { params });
    return response.data;
  },
  getProviders: async (): Promise<CashflowProvider[]> => {
    const response = await apiClient.get<CashflowProvider[]>('/cashflow/providers');
    return response.data;
  },
  createProvider: async (dto: CreateProviderDto): Promise<CashflowProvider> => {
    const response = await apiClient.post<CashflowProvider>('/cashflow/providers', dto);
    return response.data;
  },
  updateProvider: async (id: string, dto: UpdateProviderDto): Promise<CashflowProvider> => {
    const response = await apiClient.patch<CashflowProvider>(`/cashflow/providers/${id}`, dto);
    return response.data;
  },
  deleteProvider: async (id: string): Promise<void> => {
    await apiClient.delete(`/cashflow/providers/${id}`);
  },
  createSettlement: async (dto: CreateSettlementDto): Promise<Settlement> => {
    const response = await apiClient.post<Settlement>('/cashflow/settlements', dto);
    return response.data;
  },
  updateSettlement: async (id: string, dto: UpdateSettlementDto): Promise<Settlement> => {
    const response = await apiClient.patch<Settlement>(`/cashflow/settlements/${id}`, dto);
    return response.data;
  },
  deleteSettlement: async (id: string): Promise<void> => {
    await apiClient.delete(`/cashflow/settlements/${id}`);
  },
  previewStatement: async (file: File): Promise<StatementPreview> => {
    const response = await apiClient.post<StatementPreview>('/cashflow/statement/preview', statementForm(file), MULTIPART);
    return response.data;
  },
  importStatement: async (file: File): Promise<StatementImportResult> => {
    const response = await apiClient.post<StatementImportResult>('/cashflow/statement/import', statementForm(file), MULTIPART);
    return response.data;
  },
  createAccrual: async (dto: CreateAccrualDto): Promise<Accrual> => {
    const response = await apiClient.post<Accrual>('/cashflow/accruals', dto);
    return response.data;
  },
  updateAccrual: async (id: string, dto: UpdateAccrualDto): Promise<Accrual> => {
    const response = await apiClient.patch<Accrual>(`/cashflow/accruals/${id}`, dto);
    return response.data;
  },
  deleteAccrual: async (id: string): Promise<void> => {
    await apiClient.delete(`/cashflow/accruals/${id}`);
  },
};
